import { SetStateAction } from 'react'
import { Button } from '../../../../../common/components/button/Button'
import { OrderInterface } from '../../../../../common/types/interfaces'
import classes from './Form.module.sass'
import { useForm } from './hooks/useForm'
import { format } from 'date-fns'
import { IconButton } from '../../../../../common/components/iconButton/IconButton'
import close from '../../../../../assets/images/icons/close.svg'
import add from '../../../../../assets/images/icons/add.svg'
import { StatusSelector } from './statusSelector/StatusSelector'
import { createTypeSelectList } from '../../../../../common/utils/functions/createTypeSelectList'
import {
  DeliveryType,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
} from '../../../../../common/types/enums'
import {
  getDeliveryTypeColor,
  getDeliveryTypeText,
  getOrderStatusColor,
  getOrderStatusText,
  getPaymentMethodColor,
  getPaymentMethodText,
  getPaymentStatusColor,
  getPaymentStatusText,
} from '../../../../../common/utils/functions/enumToText'
import { SearchSelect } from '../../../../../common/components/searchSelect/SearchSelect'
import { useSelector } from 'react-redux'
import { selectDarkTheme } from '../../../../../store/slices/settingsSlice'
import { initialData } from '../../hooks/useOrder'
import { GMapInput } from '../../../../../common/components/gMapInput/GMapInput'
import { Input } from '../../../../../common/components/input/Input'
import { Textarea } from '../../../../../common/components/textarea/Textarea'

type Props = {
  edit: boolean
  data?: OrderInterface
  setData: React.Dispatch<SetStateAction<OrderInterface | undefined>>
  getList: () => void
  setModalActive: React.Dispatch<SetStateAction<boolean>>
  el?: OrderInterface
  initialData: OrderInterface
  modalActive: boolean
}

export const Form = ({ edit, data, setData, getList, setModalActive, el, modalActive }: Props) => {
  const {
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    deleteItem,
    adding,
    setAdding,
    createSaleItemSelectList,
    handleAddItem,
    handleNumberInput,
    handleDeliveryFeeInput,
    gMapInputHandler,
    listRef,
    createStoreSelectList,
    getDisabledStatuses,
  } = useForm(initialData, setData, edit, getList, setModalActive, modalActive, data, el)

  const dark = useSelector(selectDarkTheme)

  if (data)
    return (
      <div className={classes.container}>
        <div className={classes.title}>{el ? 'редактирование заказа' : 'создание заказа'}</div>
        {el && (
          <div className={classes.id}>
            <div className={classes.label}>id</div>
            <div className={classes.value}>{el.id}</div>
          </div>
        )}
        {data.phone_number || data.baseAddress ? (
          <div className={classes.title}>данные пользователя</div>
        ) : (
          <></>
        )}
        {el?.phone_number || !data.id ? (
          <Input
            type={'text'}
            fieldName={'phone_number'}
            value={String(data.phone_number)}
            placeHolder={'Телефон заказчика'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('phone_number')}
            onBlur={setDirty}
          />
        ) : (
          <></>
        )}

        {data.baseAddress && (
          <GMapInput
            fieldName="address"
            placeHolder="Введите адрес заказчика и выберете из списка"
            data={data.baseAddress}
            setData={gMapInputHandler}
            required
          />
        )}

        <div>
          <SearchSelect
            disabled={Boolean(data.status !== OrderStatus.NEW && data.id)}
            onBlur={setDirty}
            fieldName="storeId"
            options={createStoreSelectList()}
            value={Number(data.storeId)}
            onChangeHandler={inputHandler}
            type="text"
            placeholder={
              createStoreSelectList().length
                ? 'Начните ввод и выберите торговую точку из списка'
                : 'Сначала создайте торговые точки'
            }
            itemValidationResult={getValidationResult('storeId')}
          />
          {/* {data?.status === OrderStatus.NEW && !data?.storeId && (
            <div className={classes.hint}>
              ⚠️ Чтобы перевести заказ в сборку, выберите точку отгрузки
            </div>
          )} */}
        </div>
        <div className={classes.title}>тип доставки</div>
        <StatusSelector
          current={Number(data?.deliveryType)}
          options={createTypeSelectList(DeliveryType, getDeliveryTypeText)}
          onClick={(value: number) => inputHandler(value, 'deliveryType', 'number')}
          colorSelector={getDeliveryTypeColor}
          disabledOptions={
            data?.status && data.status > OrderStatus.NEW
              ? [DeliveryType.DELIVERY, DeliveryType.PICKUP, DeliveryType.UNSET]
              : []
          }
        />

        <div className={classes.title}>способ оплаты</div>
        <StatusSelector
          current={Number(data?.paymentMethod)}
          options={createTypeSelectList(PaymentMethod, getPaymentMethodText)}
          onClick={(value: number) => inputHandler(value, 'paymentMethod', 'number')}
          colorSelector={getPaymentMethodColor}
          disabledOptions={
            data?.paymentStatus === PaymentStatus.PAID
              ? [
                  PaymentMethod.CARD_ON_DELIVERY,
                  PaymentMethod.CASH_ON_DELIVERY,
                  PaymentMethod.UNSET, // неизвестно
                  PaymentMethod.ONLINE, // онлайн
                ]
              : []
          }
        />
        <div className={classes.title}>статус заказа</div>
        <StatusSelector
          disabledOptions={getDisabledStatuses(!!data?.storeId)}
          current={Number(data?.status)}
          options={createTypeSelectList(OrderStatus, getOrderStatusText)}
          onClick={(value: number) => inputHandler(value, 'status', 'number')}
          colorSelector={getOrderStatusColor}
        />
        <div className={classes.title}>статус оплаты</div>
        <StatusSelector
          colorSelector={getPaymentStatusColor}
          current={Number(data.paymentStatus)}
          options={createTypeSelectList(PaymentStatus, getPaymentStatusText)}
          onClick={(value: number) => inputHandler(value, 'paymentStatus', 'number')}
        />
        {el && (
          <div className={classes.row}>
            {el.createdAt && (
              <div className={classes.item}>
                <div className={classes.label}>Создан</div>
                <div className={classes.value}>
                  {format(new Date(el.createdAt), 'dd.MM.yyy HH:mm')}
                </div>
              </div>
            )}
            {el.updatedAt && (
              <div className={classes.item}>
                <div className={classes.label}>Обновлен</div>
                <div className={classes.value}>
                  {format(new Date(el.updatedAt), 'dd.MM.yyy HH:mm')}
                </div>
              </div>
            )}
          </div>
        )}

        <>
          <div className={classes.title}>информация</div>

          <Textarea
            type={'textarea'}
            fieldName={'description'}
            value={String(data.description)}
            placeHolder="Комментарии заказчика, видны заказчику"
            rows={2}
            cols={20}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('description')}
            onBlur={setDirty}
          />

          <Textarea
            type={'textarea'}
            fieldName={'notes'}
            value={String(data.notes)}
            placeHolder="Внутренние заметки, скрыты от заказчика"
            rows={2}
            cols={20}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('description')}
            onBlur={setDirty}
          />
        </>

        <div className={classes.row}>
          <div className={classes.item}>
            <div className={classes.label}>Стоимость доставки</div>
            <input
              type="text"
              value={data.deliveryFee === 0 ? '' : String(data.deliveryFee)}
              onChange={(e) => handleDeliveryFeeInput(e.target.value)}
              className={dark ? classes.inputDark : classes.input}
            />
          </div>
          <div className={classes.item}>
            <div className={classes.label}>Сумма заказа</div>
            <div className={classes.value}>{data.total?.toLocaleString('ru-RU')} ₽</div>
          </div>
        </div>
        <div className={classes.title}>состав заказа</div>
        {data.items && data.items.length > 0 && (
          <div className={classes.list} ref={listRef}>
            {data.items.map((item) => (
              <div key={item.itemId || item.id}>
                <div className={classes.row}>
                  <div className={classes.content}>
                    <div className={classes.name}>
                      <div className={classes.label}>Наименование</div>
                      <div className={classes.itemValue}>{item.name}</div>
                    </div>

                    <div className={classes.item}>
                      <div className={classes.label}>Количество</div>
                      {!item.added ? (
                        <input
                          type="text"
                          value={item.quantity === 0 ? '' : String(item.quantity)}
                          onChange={(e) =>
                            handleNumberInput(item.itemId, 'quantity', e.target.value)
                          }
                          className={dark ? classes.inputDark : classes.input}
                        />
                      ) : (
                        <div className={classes.itemValue}>{item.quantity}</div>
                      )}
                    </div>

                    <div className={classes.item}>
                      <div className={classes.label}>Цена</div>
                      {!item.added ? (
                        <input
                          type="text"
                          value={item.price === 0 ? '' : String(item.price)}
                          onChange={(e) => handleNumberInput(item.itemId, 'price', e.target.value)}
                          className={dark ? classes.inputDark : classes.input}
                        />
                      ) : (
                        <div className={classes.itemValue}>
                          {item.price.toLocaleString('ru-RU')} ₽
                        </div>
                      )}
                    </div>

                    <div className={classes.item}>
                      <div className={classes.label}>Сумма</div>
                      <div className={classes.itemValue}>
                        {item.total.toLocaleString('ru-RU')} ₽
                      </div>
                    </div>
                  </div>
                  <IconButton
                    small
                    onClick={() => deleteItem(item.itemId)}
                    alt="Удалить"
                    display
                    icon={close}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {!adding ? (
          <IconButton small onClick={() => setAdding(true)} alt="Добавить" display icon={add} />
        ) : (
          <div>
            <SearchSelect
              onBlur={setDirty}
              fieldName="saleItemId"
              options={createSaleItemSelectList()}
              onChangeHandler={handleAddItem}
              type="add_item"
              placeholder={
                createSaleItemSelectList().length
                  ? 'Начните ввод и выберите товар из списка'
                  : 'Товары не найдены'
              }
            />
            <IconButton small onClick={() => setAdding(false)} alt="Отмена" display icon={close} />
          </div>
        )}

        <Button text={el ? 'Сохранить' : 'Создать'} onClick={formAction} disabled={disabled} />
      </div>
    )
}
